import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Divider, Grid, Stack, Typography } from '@mui/material';

import { InventoryLocationApi } from '@/api/api.inventory.locations';
import { FormAutocompleteField, FormInputField } from '@/components';
import { IInventoryLocation } from '@/models/inventory.locations';
import { ConfirmTypes, useToastStore } from '@/store';
import { useUserStore } from '@/store/store.user';

import { CustodyActionSection } from './CustodyActionSection';
import { LinkedInventoryLocationItem } from './LinkedInventoryLocationItem';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import {
  getCustodyActionDefaultValues,
  normalizeCustodyActionDateTime,
  useCustodyOfficerOptions,
} from './useCustodyActionForm';
import { CustodyActions, ICustodyAction } from '../../data';
import { useKasePropertyContext } from '../../PropertyContext';

interface ICheckInFormValues {
  actionDateTime: string;
  inventoryLocation: string;
  officer: string;
  notes: string;
}

interface ICheckInForm {
  custodyAction: ICustodyAction;
}

export const CheckInForm = ({ custodyAction }: ICheckInForm) => {
  const { currentPropertyId, custodyModalValues, parentId, sourceAdapter } =
    useKasePropertyContext();
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const [inventoryLocations, setInventoryLocations] = useState<
    IInventoryLocation[]
  >([]);
  const { officerOptions } = useCustodyOfficerOptions();
  const [currentInventoryLocation, setCurrentInventoryLocation] =
    useState<IInventoryLocation>();
  const methods = useForm<ICheckInFormValues>({
    defaultValues: {
      ...getCustodyActionDefaultValues(user),
      inventoryLocation: '',
      notes: '',
    },
    mode: 'all',
  });
  const { getValues, setValue, trigger } = methods;

  useEffect(() => {
    fetchInventoryLocations();
  }, []);

  const inventoryLocationOptions = useMemo(() => {
    return inventoryLocations.map(({ _id, name }) => ({
      label: name,
      value: String(_id),
    }));
  }, [inventoryLocations?.length]);

  const handleChange = (field: string, value: string | string[]) => {
    if (field === 'inventoryLocation') {
      const selectedInventoryLocation = inventoryLocations.find(
        ({ _id }) => _id === value,
      );
      setCurrentInventoryLocation(selectedInventoryLocation);
    }
  };

  const fetchInventoryLocations = async () => {
    try {
      const res = await InventoryLocationApi.list({ limit: 1000 });
      setInventoryLocations(res.data.results);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onCheckIn = async () => {
    const isValid = await trigger();
    const { bulkSelection } = custodyModalValues;
    // If bulkSelection is not undefined, it means user wants to Check In or Move Location for multiple selected Properties
    const properties =
      bulkSelection ?? (currentPropertyId ? [currentPropertyId] : []);
    if (isValid && parentId && properties) {
      const values = getValues();
      values.actionDateTime = normalizeCustodyActionDateTime(
        values.actionDateTime,
      ) as string;
      const apiFunc =
        custodyAction === CustodyActions.CHECK_IN
          ? sourceAdapter.custodyCheckIn
          : sourceAdapter.custodyMoveLocation;
      await apiFunc(parentId, properties, values);
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: `Property is ${
          custodyAction === CustodyActions.CHECK_IN
            ? 'checked in'
            : 'moved location'
        } successfully.`,
      });
      return true;
    }
    return false;
  };

  const handleRemoveCurrentInventoryLocation = () => {
    setValue('inventoryLocation', '');
    setCurrentInventoryLocation(undefined);
  };

  return (
    <FormProvider {...methods}>
      <Stack>
        <SideModalTitle>
          {custodyAction.split('_').join(' ').toLowerCase()}
        </SideModalTitle>

        <Grid container>
          <Grid item md={12}>
            <CustodyActionSection officerOptions={officerOptions} />
            <FormAutocompleteField
              name="inventoryLocation"
              label="Inventory Location"
              items={inventoryLocationOptions}
              handleChange={handleChange}
              rules={{
                required: 'This field is required.',
              }}
              showPopupIcon
            />
            <Divider sx={{ my: 2 }} />
            {!!currentInventoryLocation && (
              <>
                <Typography
                  component="h2"
                  variant="inherit"
                  fontWeight="500"
                  sx={{ mb: 1 }}
                >
                  Inventory Location
                </Typography>
                <LinkedInventoryLocationItem
                  item={currentInventoryLocation}
                  onRemove={handleRemoveCurrentInventoryLocation}
                />
              </>
            )}
            <FormInputField
              name="notes"
              label="Notes"
              rows={6}
              sx={{ mt: 2 }}
              multiline
            />
          </Grid>
        </Grid>
        <PropertyModalFooter onSave={onCheckIn} />
      </Stack>
    </FormProvider>
  );
};
